/**
 * @file Shared parent-board table view shell helpers.
 *
 * Callers own item selection and domain-specific cell formatting. This module
 * owns grouped table sections, empty/error states, headers, and closed slots.
 */

import { appData, state } from "../../app/state/state.js";
import {
  getFilterDefinitions,
  getFilterValueCount,
  hasActiveFilters,
  hasActiveSearch,
} from "./boardRuntime.js";
import { buildGroupedItems } from "./groupedViewEngine.js";
import { getBoardCollectionNameForItemType } from "./boardRegistry.js";

const DATE_SORT_COLUMN_FALLBACKS = Object.freeze({
  tasks: Object.freeze(["due_date"]),
  patients: Object.freeze(["next_action_required", "next_appointment", "first_seen"]),
  projects: Object.freeze(["next_due_date", "delivery_date"]),
  assets: Object.freeze(["next_service_due", "purchase_date"]),
  maintenance: Object.freeze(["due_date", "completed_on"]),
});
const ACTION_COLUMN_WIDTH_REM = 3;
const DEFAULT_COLUMN_WIDTH_WEIGHT = 120;
const MIN_COLUMN_WIDTH_WEIGHT = 72;

function getCollectionTypeForItemType(itemType) {
  return getBoardCollectionNameForItemType(itemType);
}

function getViewKeyForItemType(itemType) {
  return getCollectionTypeForItemType(itemType) || `${itemType}s`;
}

function resolveDisplayColumnForRuntimeKey({
  runtimeKey,
  viewKey,
  visibleColumns,
}) {
  if (!runtimeKey) {return "";}
  if (visibleColumns.includes(runtimeKey)) {return runtimeKey;}
  if (runtimeKey !== "date") {return "";}

  return (DATE_SORT_COLUMN_FALLBACKS[viewKey] || [])
    .find((columnKey) => visibleColumns.includes(columnKey)) || "";
}

function getActiveSortColumnKey({
  activeSortKey,
  itemType,
  visibleColumns,
}) {
  const viewKey = getViewKeyForItemType(itemType);
  return resolveDisplayColumnForRuntimeKey({
    runtimeKey: activeSortKey,
    viewKey,
    visibleColumns,
  });
}

function getActiveFilterCountsByColumnKey({
  settings,
  viewKey,
  visibleColumns,
}) {
  const countsByColumnKey = new Map();

  getFilterDefinitions(viewKey).forEach((definition) => {
    const count = getFilterValueCount(settings.filters?.[definition.key], definition);
    if (count <= 0) {return;}

    const columnKey = resolveDisplayColumnForRuntimeKey({
      runtimeKey: definition.key,
      viewKey,
      visibleColumns,
    });
    if (!columnKey) {return;}

    countsByColumnKey.set(columnKey, (countsByColumnKey.get(columnKey) || 0) + count);
  });

  return countsByColumnKey;
}

function renderHeaderSortIndicator({ sortDirection }) {
  const icon = sortDirection === "desc" ? "arrow_downward" : "arrow_upward";
  const label = sortDirection === "desc" ? "Sorted descending" : "Sorted ascending";

  return `
        <span
            class="material-symbols-outlined shrink-0 text-[14px] leading-none text-[var(--dfc-brand-accent)]"
            aria-label="${label}"
            title="${label}"
        >${icon}</span>
    `;
}

function renderHeaderFilterCount(count) {
  if (count <= 0) {return "";}

  return `<span class="shrink-0 text-[11px] font-semibold leading-none text-[var(--dfc-brand-accent)]">(${count})</span>`;
}

function getColumnWidthWeight(columnKey, columnWidths = {}) {
  const savedWidth = Number(columnWidths[columnKey]);
  if (!Number.isFinite(savedWidth) || savedWidth <= 0) {
    return DEFAULT_COLUMN_WIDTH_WEIGHT;
  }
  return Math.max(savedWidth, MIN_COLUMN_WIDTH_WEIGHT);
}

function getColumnWidthStyle({
  columnKey,
  columnWidths,
  visibleColumns,
}) {
  const totalWeight = visibleColumns.reduce(
    (total, key) => total + getColumnWidthWeight(key, columnWidths),
    0,
  );
  const columnWeight = getColumnWidthWeight(columnKey, columnWidths);

  if (totalWeight <= 0) {return "";}

  return `style="width: ${(columnWeight / totalWeight) * 100}%"`;
}

function getActionColumnHeaderHtml(title) {
  return `<th scope="col" class="sticky right-0 z-[2] w-12 bg-gray-50 px-2 py-3 text-center" style="width: ${ACTION_COLUMN_WIDTH_REM}rem;" title="${title}"></th>`;
}

function shouldRenderClosedLoadMore(group, itemType) {
  const collectionType = getCollectionTypeForItemType(itemType);
  if (!collectionType) {
    return false;
  }
  const paginationState = state.closedItemsPagination?.[collectionType];
  if (!state.showClosed || !paginationState?.hasMore) {
    return false;
  }
  return group?.id === "closed" || group?.isClosed === true;
}

function renderClosedLoadMoreButton(itemType) {
  const collectionType = getCollectionTypeForItemType(itemType);
  if (!collectionType) {
    return "";
  }

  return `
            <div class="mt-4 text-center pb-2">
                <button
                    type="button"
                    class="px-6 py-2 bg-white border border-gray-200 text-gray-600 rounded-full transition-colors hover:bg-gray-50 hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)] text-sm font-medium shadow-sm load-more-closed-btn"
                    data-collection-type="${collectionType}"
                >
                    Load More
                </button>
            </div>
        `;
}

function getBoardEmptyStateCopy(viewKey, singularLabel, sourceItems = [], renderedItems = []) {
  const hasSearch = hasActiveSearch(viewKey);
  const hasFilters = hasActiveFilters(viewKey);
  const sourceCount = Array.isArray(sourceItems) ? sourceItems.length : 0;
  const renderedCount = Array.isArray(renderedItems) ? renderedItems.length : 0;

  if (renderedCount > 0) {
    return null;
  }

  if ((hasSearch || hasFilters) && sourceCount > 0) {
    return {
      title: "No results",
      body: hasSearch && hasFilters ?
        "No items match the current search and filters." :
        hasSearch ?
          "No items match the current search." :
          "No items match the current filters.",
    };
  }

  return {
    title: `No ${singularLabel}s yet`,
    body: `There are no ${singularLabel}s to display on this board yet.`,
  };
}

/**
 * Renders an empty/error state card for a board table.
 * @param {object} options - State card options.
 * @param {string} options.title - State title.
 * @param {string} options.body - State body.
 * @param {string} [options.tone] - State tone.
 * @returns {string} State card HTML.
 */
export function renderBoardTableStateCard({ title, body, tone = "empty" }) {
  const icon = tone === "error" ? "error" : "inbox";
  const iconClasses = tone === "error" ? "text-red-400" : "text-gray-300";

  return `
        <div class="rounded-lg border border-gray-200 bg-white px-6 py-10 text-center shadow-sm">
            <span class="material-symbols-outlined text-[32px] ${iconClasses}">${icon}</span>
            <h3 class="mt-3 text-base font-semibold text-gray-800">${title}</h3>
            <p class="mt-1 text-sm text-gray-500">${body}</p>
        </div>
    `;
}

/**
 * Renders a board-table header from visible column settings.
 * @param {object} options - Header options.
 * @param {Array<string>} options.visibleColumns - Visible column keys.
 * @param {Object<string, string>} options.columnLabels - Column label map.
 * @param {Object<string, number>} [options.columnWidths] - Saved widths.
 * @param {string} options.itemType - Table item type.
 * @param {boolean} options.showTaskQuickClose - Whether quick-close column appears.
 * @returns {string} Table header HTML.
 */
export function renderBoardTableHeader({
  visibleColumns,
  columnLabels,
  columnWidths = {},
  itemType,
  showTaskQuickClose,
}) {
  const viewKey = getViewKeyForItemType(itemType);
  const settings = state.viewSettings?.[viewKey] || {};
  const activeSortColumnKey = getActiveSortColumnKey({
    activeSortKey: settings.sortBy || "",
    itemType,
    visibleColumns,
  });
  const activeFilterCountsByColumnKey = getActiveFilterCountsByColumnKey({
    settings,
    viewKey,
    visibleColumns,
  });
  const sortDirection = settings.sortDirection || "asc";

  return `<thead class="border-y border-slate-100 bg-gray-50 text-xs uppercase text-gray-700">
        <tr>
            ${visibleColumns.map((key, index) => {
    const title = columnLabels[key] || key;
    const neighborColumnKey = visibleColumns[index + 1] || "";
    const widthStyle = getColumnWidthStyle({
      columnKey: key,
      columnWidths,
      visibleColumns,
    });
    const isActiveSort = activeSortColumnKey === key;
    const activeFilterCount = activeFilterCountsByColumnKey.get(key) || 0;
    const isActiveFilter = activeFilterCount > 0;
    const headerTextClass = isActiveSort || isActiveFilter ?
      "text-[var(--dfc-brand-accent)]" :
      "text-gray-700";

    return `<th scope="col" class="sortable group relative bg-gray-50 px-6 py-3 text-left font-semibold tracking-wider" draggable="true" data-key="${key}" ${widthStyle}>
                    <div class="flex min-w-0 items-center gap-1.5">
                        <span class="block min-w-0 truncate ${headerTextClass}">${title}</span>
                        ${isActiveSort ? renderHeaderSortIndicator({ sortDirection }) : ""}
                        ${renderHeaderFilterCount(activeFilterCount)}
                    </div>
                    ${neighborColumnKey ? `<div class="table-resizer hidden sm:block" data-key="${key}" data-neighbor-key="${neighborColumnKey}"></div>` : ""}
                </th>`;
  }).join("")}
            ${(itemType !== "task") ? getActionColumnHeaderHtml("Add Task") : ""}
            ${showTaskQuickClose ? getActionColumnHeaderHtml("Quick close") : ""}
        </tr>
    </thead>`;
}

/**
 * Renders the grouped table section shell.
 * @param {Array<object>} items - Runtime-filtered table items.
 * @param {object} options - Grouped table options.
 * @param {string} options.viewKey - Board view key.
 * @param {string} options.itemType - Item type.
 * @param {string} options.singularLabel - Singular item label.
 * @param {Array<object>} options.sourceItems - Source items before runtime filters.
 * @param {string} options.groupBy - Grouping mode.
 * @param {string} options.dateGroupingMode - Date grouping mode.
 * @param {Array<string>} options.activeFilterKeys - Active filter keys.
 * @param {Function} options.renderTable - Table renderer callback.
 * @returns {string} Grouped table HTML.
 */
export function renderGroupedTableSections(items, {
  viewKey,
  itemType,
  singularLabel,
  sourceItems,
  groupBy,
  dateGroupingMode,
  activeFilterKeys,
  renderTable,
}) {
  const groups = buildGroupedItems({
    itemType,
    items,
    groupBy,
    dateGroupingMode,
    showClosed: state.showClosed || false,
    taskList: appData.tasks,
  });

  const visibleGroups = groups.filter((group) => group.items.length > 0);

  if (visibleGroups.length === 0) {
    const emptyState = getBoardEmptyStateCopy(viewKey, singularLabel, sourceItems, items);
    return renderBoardTableStateCard(emptyState || {
      title: "Nothing to show",
      body: "There is nothing to display right now.",
    });
  }

  return visibleGroups.map((group, index) => {
    const marginTopClass = index === 0 ? "" : "mt-6";
    const groupLabel = group.title || "Unknown Group";
    const colorIndicator = group.color ?
      `<span class="inline-block w-3 h-3 rounded-full mr-2 align-middle" style="background-color: ${group.color};"></span>` :
      "";

    return `
            <div class="bg-white rounded-lg shadow overflow-hidden ${marginTopClass}" data-group-id="${group.id}">
                <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h3 class="text-xl font-semibold text-gray-900">
                        ${colorIndicator}${groupLabel}
                        <span class="text-sm font-normal text-gray-500 ml-2">${group.items.length}</span>
                    </h3>
                    <button
                        type="button"
                        class="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                        data-group-toggle="${group.id}"
                        aria-label="Collapse ${groupLabel}"
                        aria-expanded="true"
                    >
                        <span class="material-symbols-outlined pointer-events-none text-[20px]">expand_more</span>
                    </button>
                </div>
                <div class="overflow-x-auto md:overflow-x-hidden" data-group-content="${group.id}">
                    ${renderTable(group.items, itemType, activeFilterKeys)}
                </div>
            </div>
            ${shouldRenderClosedLoadMore(group, itemType) ? renderClosedLoadMoreButton(itemType) : ""}
        `;
  }).join("");
}
